import React, { useState } from 'react';
import { Button, Card, Skeleton, Table, Tag, Typography, theme } from 'antd';
import type { TableColumnsType } from 'antd';
import { Link } from 'react-router-dom';
import { CheckCircleOutlined, DownOutlined, UpOutlined, WarningOutlined } from '@ant-design/icons';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { VOIP_CALLS_PAGE_PATH } from '../../../pages/leedflow/VoipCallsPage/VoipCallsPage';
import { formatCallNumber, renderDate, renderDirection, renderPlatform } from './callCells';
import RawDocumentModal from '../../@shared/RawDocumentModal';
import type {
  CallsAggregate,
  FaultyCallEntry,
} from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

const { useToken } = theme;

interface FaultyCallsPanelProps {
  faulty: CallsAggregate['faulty'];
  /**
   * Échantillon des plus récents, plafonné côté backend. Absent sur l'onglet
   * Fiabilité, dont la route ne renvoie que le compteur : le panneau se réduit
   * alors au compteur, sans table vide.
   */
  rows?: FaultyCallEntry[];
  status: ContextStatus;
}

const LEED_1717_URL = 'https://linear.app/leed/issue/LEED-1717/callr-un-tiers-des-docs-dappel-restent-bloques-en-pending';

// Motifs connus. Une clé absente de cette table (nouvelle famille de défaut)
// tombe sur le fallback de `labelForReason` et s'affiche telle quelle plutôt
// que de disparaître : `byReason` est explicitement extensible.
const REASON_LABELS: Record<string, string> = {
  callr_pending: 'En attente Callr, jamais résolu',
};

const labelForReason = (reason: string): string => REASON_LABELS[reason] ?? reason;

const buildRows = (byReason: Record<string, number>) =>
  Object.entries(byReason).sort((a, b) => b[1] - a[1]);

const FaultyCallsPanel: React.FC<FaultyCallsPanelProps> = ({ faulty, rows = [], status }) => {
  const [inspectedId, setInspectedId] = useState<string | null>(null);
  const { token } = useToken();

  // Replié par défaut : le détail n'a d'intérêt qu'au moment où on enquête,
  // mais le compteur reste dans le titre en permanence — c'est lui qui doit
  // rester sous les yeux, sinon le défaut sort du radar (LEED-1717).
  const [isOpen, setIsOpen] = useState(false);

  // Cible explicite à zéro. Masquer ces documents les ferait sortir du radar
  // pendant qu'ils continuent d'être produits en production (LEED-1717).
  const isClean = faulty.total === 0;

  const isBodyHidden = !isOpen && status !== ContextStatus.LOADING;

  // Pas de colonne durée ni état : un défaut n'en a pas d'exploitable, les
  // afficher inventerait une donnée. Les numéros et la plateforme, eux, sont
  // ce qui permet de reconnaître d'où vient le défaut.
  const columns: TableColumnsType<FaultyCallEntry> = [
    { title: 'Date', dataIndex: 'created', key: 'created', render: (created: number) => renderDate(created) },
    {
      title: 'Type',
      dataIndex: 'direction',
      key: 'direction',
      render: (direction: string | null) => renderDirection(direction),
    },
    {
      title: 'N° appelant',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: string | null) => formatCallNumber(customer),
    },
    {
      title: 'N° appelé',
      dataIndex: 'sender',
      key: 'sender',
      render: (sender: string | null) => formatCallNumber(sender),
    },
    {
      title: 'Plateforme',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string | null) => renderPlatform(platform),
    },
    {
      title: 'Motif',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason: string) => labelForReason(reason),
    },
  ];

  return (
    <Card
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Appels en défaut
          {status !== ContextStatus.LOADING && (
            <Tag color={isClean ? 'success' : 'warning'} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(faulty.total)}
            </Tag>
          )}
        </span>
      }
      // Replié, le corps de la Card laisserait une bande vide de son padding.
      styles={isBodyHidden ? { body: { display: 'none' } } : undefined}
      extra={
        status === ContextStatus.LOADING ? null : (
          <Button type="link" size="small" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <>Masquer <UpOutlined /></> : <>Afficher <DownOutlined /></>}
          </Button>
        )
      }
    >
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : !isOpen ? null : isClean ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '1rem 0' }}>
          <CheckCircleOutlined style={{ fontSize: 22, color: token.colorSuccess }} />
          <div>
            <div style={{ fontWeight: 500 }}>Aucun appel en défaut</div>
            <Typography.Text type="secondary">
              Ce compteur cible zéro : une régression redeviendrait visible ici.
            </Typography.Text>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <WarningOutlined style={{ fontSize: 22, color: token.colorWarning }} />
            <span style={{ fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(faulty.total)}
            </span>
            <Typography.Text type="secondary">exclus de toutes les moyennes de l'onglet Général</Typography.Text>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {buildRows(faulty.byReason).map(([reason, count]) => (
              <div
                key={reason}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  borderRadius: token.borderRadiusSM,
                  padding: '6px 10px',
                  background: token.colorFillTertiary,
                }}
              >
                <span>{labelForReason(reason)}</span>
                <span style={{ color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(count)}
                </span>
              </div>
            ))}
          </div>

          {rows.length > 0 && (
            <>
              <Table
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                size="small"
                scroll={{ x: true }}
                // Le document brut plutôt qu'une fiche mise en forme : ces
                // lignes existent pour être diagnostiquées, et c'est le champ
                // qu'on n'a pas pensé à afficher qu'on cherchera.
                onRow={(record) => ({
                  onClick: () => setInspectedId(record.id),
                  style: { cursor: 'pointer' },
                })}
              />

              {/* Dire ce que la table ne montre pas : sans cette ligne, 50
                  lignes affichées sous un compteur à 636 se lisent comme un
                  désaccord. */}
              {faulty.total > rows.length && (
                <Typography.Text type="secondary">
                  Les {formatNumber(rows.length)} plus récents sur {formatNumber(faulty.total)}.
                </Typography.Text>
              )}
            </>
          )}

          <Typography.Text type="secondary">
            Ce sont des documents d'appel créés au routage et jamais résolus, pas des appels ratés
            ; détail appel par appel sur la page{' '}
            <Link to={VOIP_CALLS_PAGE_PATH}>VoIP</Link>, correctif suivi sur{' '}
            <a href={LEED_1717_URL} target="_blank" rel="noreferrer">
              LEED-1717
            </a>
            .
          </Typography.Text>
        </div>
      )}

      <RawDocumentModal
        collection="Calls"
        documentId={inspectedId}
        onClose={() => setInspectedId(null)}
      />
    </Card>
  );
};

export default FaultyCallsPanel;
